import type {
  AttributesFor,
  BodyByPath,
  QueryParamsByPath,
  ResponseByPath,
  SchemaAttributes,
  Schemas,
} from '@@/api/types.js'

export const API_CLIENTS_LIST_URL = '/{provet_id}/api/clients'
export const API_CLIENTS_DETAIL_URL = '/{provet_id}/api/clients/{id}'
export const API_CLIENTS_BALANCE_URL = '/{provet_id}/api/clients/{id}/balance'
export const API_CLIENTS_CHECK_DUPLICATE_URL =
  '/{provet_id}/api/clients/check_duplicate'

export type ClientListQueryParams = QueryParamsByPath<
  'get',
  typeof API_CLIENTS_LIST_URL
>
export type ClientListResponse = ResponseByPath<
  'get',
  typeof API_CLIENTS_LIST_URL
>
export type ClientCreateBody = BodyByPath<'post', typeof API_CLIENTS_LIST_URL>
export type ClientCreateResponse = ResponseByPath<
  'post',
  typeof API_CLIENTS_LIST_URL
>

export type ClientDetailQueryParams = QueryParamsByPath<
  'get',
  typeof API_CLIENTS_DETAIL_URL
>
export type ClientDetailResponse = ResponseByPath<
  'get',
  typeof API_CLIENTS_DETAIL_URL
>

export type ClientMatchQueryParams = QueryParamsByPath<
  'get',
  typeof API_CLIENTS_CHECK_DUPLICATE_URL
>
export type ClientMatchResponse = ResponseByPath<
  'get',
  typeof API_CLIENTS_CHECK_DUPLICATE_URL
>

export type ClientBalanceResponse = ResponseByPath<
  'get',
  typeof API_CLIENTS_BALANCE_URL
>

export type ClientBalanceData = NonNullable<
  ClientBalanceResponse['data']['attributes']
>

export type CreateClient = AttributesFor<ClientCreateBody['data']>
export type CreateClientAlternativePayer =
  CreateClient['write_only_alternative_payer']
export type ClientData = SchemaAttributes<'Client'>
export type PhoneData = SchemaAttributes<'Phone'>

export type NormalizedClientMatchData = {
  id: string
  firstName: CreateClient['first_name']
  lastName: CreateClient['last_name']
  idNumber: CreateClient['id_number']
  email: CreateClient['email']
  phones: string[]
  streetAddress: CreateClient['street_address']
  streetAddress2?: NonNullable<CreateClient['street_address_2']>
  streetAddress3?: NonNullable<CreateClient['street_address_3']>
  zipCode: CreateClient['zip_code']
  city: CreateClient['city']
  customerType: CreateClient['customer_type']
  registerNumber?: CreateClient['register_number']
}

export type NormalizedClient = Schemas['Client'] & {
  phoneNumbers?: Schemas['Phone'][]
  patients?: Schemas['Patient'][]
}

export type NormalizedClientListResponse = Omit<ClientListResponse, 'data'> & {
  data: NormalizedClient[]
}

// The generated schema types write-only relationships as `{ id, type }[]` references,
// but the BE accepts full attribute payloads on create. Override those two relationships.
// @todo VCF-1589: remove this override once the BE OpenAPI schema correctly describes write-only relationship payloads
// Uses explicit interface instead of nested Omit<NonNullable<…>> to avoid O(n) type iteration
// over the large generated schema, which can push TS past its complexity budget.
export interface ClientCreateData {
  type: ClientCreateBody['data']['type']
  attributes?: ClientCreateBody['data']['attributes']
  relationships?: {
    home_department?: NonNullable<
      ClientCreateBody['data']['relationships']
    >['home_department']
    invoicing_client?: NonNullable<
      ClientCreateBody['data']['relationships']
    >['invoicing_client']
    parent_referring_organization?: NonNullable<
      ClientCreateBody['data']['relationships']
    >['parent_referring_organization']
    write_only_tag_texts?: NonNullable<
      ClientCreateBody['data']['relationships']
    >['write_only_tag_texts']
    write_only_phone_numbers?: { data: PhoneApiPayload[] }
    write_only_holding_place_numbers?: { data: HoldingPlaceNumberApiPayload[] }
  }
}

export const ClientCustomerType = {
  Private: 'CUSTOMER_TYPE_PRIVATE',
  Company: 'CUSTOMER_TYPE_COMPANY',
  EuCompany: 'CUSTOMER_TYPE_EU_COMPANY',
  CharitableOrg: 'CUSTOMER_TYPE_CHARITABLE_ORG',
  UkCharity: 'CUSTOMER_UK_CHARITY',
} as const satisfies Record<string, CreateClient['customer_type']>

export type ClientStatus = NonNullable<ClientData['status']>
export const ClientStatusOptions = {
  StatusInternal: 'STATUS_INTERNAL',
  StatusExternal: 'STATUS_EXTERNAL',
  StatusResearch: 'STATUS_RESEARCH',
} as const satisfies Record<string, ClientStatus>

export type ClientPrefillData = {
  breeder?: boolean
  idNumber?: string
  firstName?: string
  lastName?: string
  driversLicenseNumber?: string
  gender?: string
  dateOfBirth?: string
  secondaryOwnerFirstName?: string
  secondaryOwnerLastName?: string
  homeDepartment?: string
  phone?: string
  email?: string
  city?: string
  zipCode?: string
  streetAddress?: string
  streetAddress2?: string
  streetAddress3?: string
}

export const ClientTitle = {
  Mr: 'MR',
  Mx: 'MX',
  Ms: 'MS',
  Miss: 'MISS',
  Mrs: 'MRS',
  Dr: 'DR',
  Prof: 'PROF',
  Rev: 'REV',
  Dame: 'DAME',
  Lady: 'LADY',
  Lord: 'LORD',
  Sir: 'SIR',
} as const satisfies Record<string, ClientData['title']>

export type ClientGender = NonNullable<ClientData['gender']>

export const ClientGenderOptions = {
  Male: 'MALE',
  Female: 'FEMALE',
  Other: 'OTHER',
} as const satisfies Record<string, ClientGender>

export const ClientFieldName = {
  FirstName: 'firstname',
  LastName: 'lastname',
  SecondaryOwnerFirstName: 'secondary_owner_first_name',
  SecondaryOwnerLastName: 'secondary_owner_last_name',
  Title: 'title',
  HomeDepartment: 'home_department',
  OrganizationName: 'organization_name',
  CustomerType: 'customer_type',
  StreetAddress: 'street_address',
  StreetAddress2: 'street_address_2',
  StreetAddress3: 'street_address_3',
  ZipCode: 'zip_code',
  City: 'city',
  Country: 'country',
  State: 'state',
  CountryRegion: 'country_region',
  Phone: 'phone',
  Email: 'email',
  AltEmails: 'alt_emails',
  IdNumber: 'id_number',
  CriticalNotes: 'critical_notes',
  Remarks: 'remarks',
  Tags: 'tags',
  VatNumber: 'vat_number',
  RegisterNumber: 'register_number',
  Gender: 'gender',
  DateOfBirth: 'date_of_birth',
  DriversLicenceNumber: 'drivers_licence_number',
  ProductionAnimalClient: 'production_animal_client',
  FarmCode: 'farm_code',
  HoldingPlaceNumber: 'holding_place_numbers',
  ReferringOrganization: 'referring_organization',
  ReferringVet: 'referring_vet',
  ParentReferringOrganization: 'parent_referring_organization',
  Breeder: 'breeder',
  Farmer: 'farmer',
  NoSms: 'no_sms',
  NoEmail: 'no_email',
  OnStop: 'on_stop',
  Archived: 'archived',
  // Communication Preferences fields
  CommunicationPreferenceValidFrom: 'communication_preference_valid_from',
  CommunicationPreferenceValidTo: 'communication_preference_valid_to',
  CommunicationPreferenceConsent: 'communication_preference_consent',
  CommunicationPreferencePrivacyPolicyVersion:
    'communication_preference_privacy_policy_version',
  // Invoicing client fields
  InvoicingClient: 'invoicing_client',
  AlternativePayer: 'write_only_alternative_payer',
  AlternativeInvoicingAddressExist: 'alternative_invoicing_address_exist',
  EInvoiceAddress: 'einvoice_address',
  EInvoiceOperator: 'einvoice_operator',
  Status: 'status_type',
  DueDateDelay: 'due_date_delay',
} as const

export type ClientFieldName =
  (typeof ClientFieldName)[keyof typeof ClientFieldName]

export type SimpleClient = {
  id: string
  name: string
  initials: string
  isOrganisation: boolean
  email?: string
  emails: string[]
  noEmail: boolean
  noSms?: boolean
  phone?: string
  phoneId?: string
  phones: string[]
  criticalNotes?: string
  secondary?: {
    name: string
    initials: string
    isOrganisation: boolean
  }
}

/**
 * Represents an email entry in the repeatable emails field.
 */
export interface EmailEntry {
  email?: string
  isPrimary: boolean
}

/**
 * Represents a phone entry in the repeatable phones field.
 * Maps to the backend Phone model via write_only_phone_numbers.
 */
export const PhoneTypeCode = {
  LandlinePhone: 0,
  MobilePhone: 1,
} as const

export type PhoneTypeCodeValue =
  (typeof PhoneTypeCode)[keyof typeof PhoneTypeCode]

export interface PhoneEntry {
  phoneNumber?: string
  description?: string
  typeCode: PhoneTypeCodeValue
  preferredReminders: boolean // marks as primary for communication
}

/**
 * Phone payload for API requests (snake_case format).
 * Used in write_only_phone_numbers relationship when creating/updating clients.
 */
export interface PhoneApiPayload {
  type: 'Phone'
  phone_number: string
  description?: string | null
  preferred_reminders: boolean
  type_code: PhoneTypeCodeValue
}

/**
 * Phone data from API responses (snake_case format).
 * Used when reading phone_numbers relationship from clients.
 */
export interface PhoneApiResponse {
  phone_number?: PhoneApiPayload['phone_number'] | null
  description?: PhoneApiPayload['description']
  type_code?: PhoneApiPayload['type_code']
  preferred_reminders?: PhoneApiPayload['preferred_reminders']
}

export const HOLDING_PLACE_NUMBER_NAME_MAX_LENGTH = 31
export const HOLDING_PLACE_NUMBER_DESCRIPTION_MAX_LENGTH = 255

/**
 * Represents a holding place number entry in the repeatable field.
 * Maps to the backend HoldingPlaceNumber model via write_only_holding_place_numbers.
 */
export interface HoldingPlaceNumberEntry {
  name?: string // Maps to holding_place_number in API
  description?: string
  isDuplicate?: boolean
  // Fields registered by FarmLocationCreateFields (unused, ignored in API transform)
  addressType?: string
  address?: unknown
}

/**
 * Holding place number payload for API requests (snake_case format).
 * Used in write_only_holding_place_numbers relationship when creating/updating clients.
 */
export interface HoldingPlaceNumberApiPayload {
  type: 'HoldingPlaceNumbers'
  holding_place_number: string
  description?: string | null
  save_with_duplicate_holding_place_number?: boolean
}
